import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import useFormOrdererStore from './form-orderer';
import { ConnectionType, type StepCustomerData } from '../types/step-data';
import type { PhoneNumber } from '../types/common';
import type { Household } from '../types/household';

const useFormCustomerStore = defineStore('form-customer', () => {
  const formOrdererStore = useFormOrdererStore();

  const creditCheck = ref<boolean>(false);
  const customerPassword = ref<string>('');
  const extendedDataUsage = ref<boolean>(false);
  const dataUsage = ref<boolean>(true);
  const iban = ref<string>('');
  const itemizedBill = ref<string>(
    formOrdererStore.household?.customerId ? 'keine Angabe' : 'Nein',
  );
  const numberPortability = ref<boolean>(false);
  const otherConnectionOwners = ref<boolean>(false);
  const otherConnectionOwnersData = ref<Household[]>([]);
  const otherProvider = ref<string>('');
  const phoneBookEntry = ref<string>('Nein');
  const portablePhoneNumbers = ref<PhoneNumber[]>([]);
  const referralCode = ref<string>('');
  const selectedProvider = ref<string>('');
  const connectionType = ref<ConnectionType>(
    formOrdererStore.household?.customerId
      ? ConnectionType.NONE
      : ConnectionType.NEW_CONNECTION,
  );
  const telephoneAdvertisement = ref<boolean>(false);
  const wantsBankTransfer = ref<boolean>(false);
  const phoneNumberChange = ref<PhoneNumber>({} as PhoneNumber);

  const stepData = computed<StepCustomerData>(() => ({
    creditCheck: creditCheck.value,
    customerPassword: customerPassword.value,
    extendedDataUsage: extendedDataUsage.value,
    dataUsage: dataUsage.value,
    iban: iban.value,
    itemizedBill: itemizedBill.value,
    numberPortability: numberPortability.value,
    otherConnectionOwners: otherConnectionOwners.value,
    otherConnectionOwnersData: otherConnectionOwnersData.value,
    otherProvider: otherProvider.value,
    phoneBookEntry: phoneBookEntry.value,
    portablePhoneNumbers: portablePhoneNumbers.value,
    referralCode: referralCode.value,
    selectedProvider: selectedProvider.value,
    connectionType: connectionType.value,
    telephoneAdvertisement: telephoneAdvertisement.value,
    wantsBankTransfer: wantsBankTransfer.value,
    phoneNumberChange: phoneNumberChange.value,
  }));

  const isSwitchingProvider = computed(
    () => connectionType.value === ConnectionType.SWITCHING_PROVIDER,
  );

  watch(connectionType, value => {
    if (value === ConnectionType.SWITCHING_PROVIDER) {
      return;
    }
    numberPortability.value = false;
    otherConnectionOwners.value = false;
    otherProvider.value = '';
    selectedProvider.value = '';
    phoneNumberChange.value = {} as PhoneNumber;
  });

  watch(otherConnectionOwners, value => {
    if (value === false) {
      otherConnectionOwnersData.value = [];
    }
  });

  watch(numberPortability, value => {
    if (value === false) {
      portablePhoneNumbers.value = [];
    }
  });

  return {
    creditCheck,
    customerPassword,
    extendedDataUsage,
    dataUsage,
    iban,
    isSwitchingProvider,
    itemizedBill,
    numberPortability,
    otherConnectionOwners,
    otherConnectionOwnersData,
    otherProvider,
    phoneBookEntry,
    portablePhoneNumbers,
    referralCode,
    selectedProvider,
    connectionType,
    telephoneAdvertisement,
    wantsBankTransfer,
    phoneNumberChange,
    stepData,
  };
});

export default useFormCustomerStore;
